import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/track_usage"
import Sortable from "sortablejs"
import { turboStreamWrite } from "../lib/write_request"
import { afterMorph } from "../lib/after_morph"

// A reorder answers with a whole-page turbo refresh, which dumps focus on <body>.
// Where the page replaces (tasks, metrics) this controller is thrown away, so a
// keyboard move is parked in module scope and picked back up by the next
// connect(). Where the page morphs (plan view) the controller survives and the
// morph callback picks it up instead, then drops it so it can't leak to a
// later page.
let pendingMove = null

export default class extends Controller {
  static targets = ["item", "parent", "moveUp", "moveDown"]
  static values = {
    url: String,
    handleSelector: String
  }

  initialize() {
    this.handleDragStart = this.handleDragStart.bind(this)

    this.sortable = Sortable.create(this.parent, {
      animation: 150,
      handle: this.handleSelectorValue,
      ghostClass: 'sortable-ghost',
      onStart: this.onStart.bind(this),
      onEnd: this.onEnd.bind(this),
    })

    this.element.addEventListener('dragstart', this.handleDragStart)
  }

  connect() {
    this.disableAtBoundaries()
    this.restoreFocus()

    // A morph strips the disabled attributes set above.
    this.stopAfterMorph = afterMorph(this.element, () => {
      this.disableAtBoundaries()
      this.restoreFocus()
      pendingMove = null
    })
  }

  get parent() {
    if (this.hasParentTarget) { return this.parentTarget }

    return this.element
  }

  moveUp(event) { this.move(event.currentTarget, -1) }

  moveDown(event) { this.move(event.currentTarget, 1) }

  move(button, offset) {
    const items = this.itemTargets
    const item = this.itemFor(button)
    const index = items.indexOf(item)
    const swapWith = items[index + offset]

    if (!swapWith) { return }

    // Leave the DOM exactly as a finished drag leaves it, then reuse its save path.
    const [first, second] = (offset < 0) ? [item, swapWith] : [swapWith, item]
    this.parent.insertBefore(this.blockFor(first), this.blockFor(second))

    pendingMove = {
      globalId: item.dataset.dragGlobalId,
      up: offset < 0,
      position: index + offset + 1,
      total: items.length
    }

    this.updateItems()
    this.trackReorder("keyboard")
  }

  // Every list posts to the same /reorder route, so the URL can't say which
  // list moved; the items' dom_ids (goal_12, focus_area_3...) can.
  trackReorder(method) {
    const list = this.itemTargets[0]?.id.replace(/[-_]?\d+$/, "") || this.element.id || null
    track("items_reordered", { method, list })
  }

  restoreFocus() {
    if (!pendingMove) { return }

    const buttons = pendingMove.up ? this.moveUpTargets : this.moveDownTargets
    const button = buttons.find(b => this.itemFor(b)?.dataset.dragGlobalId === pendingMove.globalId)

    // Turbo may render a cached snapshot first, which still shows the old order.
    // Wait for the render where the item actually landed.
    if (!button || this.itemTargets.indexOf(this.itemFor(button)) + 1 !== pendingMove.position) { return }

    const { position, total } = pendingMove
    pendingMove = null

    // A button disabled at the boundary can't hold focus; hand it to its pair.
    const focusable = button.disabled ? button.parentElement.querySelector("button:not(:disabled)") : button
    focusable?.focus()
    this.announce(position, total)
  }

  announce(position, total) {
    const status = document.getElementById("reorder-status")

    if (!status) { return }

    status.textContent = status.dataset.template
      .replace("%{position}", position)
      .replace("%{total}", total)
  }

  disableAtBoundaries() {
    const items = this.itemTargets

    this.moveUpTargets.forEach(b => { b.disabled = this.itemFor(b) === items[0] })
    this.moveDownTargets.forEach(b => { b.disabled = this.itemFor(b) === items[items.length - 1] })
  }

  itemFor(element) {
    return element.closest("[data-drag-global-id]")
  }

  // An item is often wrapped (turbo-frame, .draggable-ownership), and it's the
  // wrapper that has to move.
  blockFor(item) {
    return Array.from(this.parent.children).find(child => child.contains(item)) || item
  }

  disconnect() {
    // for some reason, nested sortables will disconnect
    // during dragging. So do not tear down sortables
    this.element.removeEventListener('dragstart', this.handleDragStart)
    this.stopAfterMorph()
  }

  get itemsGlobalIds() {
    return Array.from(this.itemTargets).map(item => item.dataset.dragGlobalId)
  }

  onStart(_event) {
    this._onStartGlobalIds = this.itemsGlobalIds
  }

  onEnd(_event) {
    // Sortable fires onEnd for a drop back into the same slot too; that is
    // not a reorder worth counting (the save itself stays as it was).
    const moved = JSON.stringify(this._onStartGlobalIds) !== JSON.stringify(this.itemsGlobalIds)
    this.updateItems()
    if (moved) this.trackReorder("drag")
    this.cleanUp()
  }

  handleDragStart(event) {
    const draggedElement = event.target.closest('.draggable-ownership')
    const documentId = draggedElement?.dataset?.docId

    if (documentId) {
      const dataToTransfer = JSON.stringify({
        documentId: documentId
      })

      event.dataTransfer.setData("application/json", dataToTransfer)
    } else {
      console.error("Document ID not found for the dragged item.")
    }
  }

  cleanUp() {
    this._onStartGlobalIds = null
  }

  updateItems() {
    const data = { items: this.itemsGlobalIds }

    turboStreamWrite(this.urlValue, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: JSON.stringify(data)
    }, { message: "Unable to reorder. Please refresh and try again.", revert: true })
  }
}
